/**
 * TranscriptView Component
 *
 * Main transcript view component that displays all segments with auto-scroll
 * during playback, search functionality, and inline chat support.
 * Search and auto-scroll controls are managed by the parent component.
 * Design matches Magic Patterns specification with clean card-based segments.
 * Exposes a `scrollToSegment(segmentId)` imperative handle via forwardRef
 * for parent components to programmatically scroll to a specific segment.
 */

import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TranscriptSegment } from "./TranscriptSegment";
import { useAnnotations } from "@/hooks/useAnnotations";
import { useSegmentChatCounts } from "@/hooks/useStreamingChat";
import type {
  TranscriptSegmentResponse,
  SpeakerResponse,
} from "@/types/transcript";
import type { SegmentAnnotationSummary } from "@/types/annotation";

/** Imperative handle exposed by TranscriptView via ref */
export interface TranscriptViewHandle {
  scrollToSegment: (segmentId: string) => void;
}

interface TranscriptViewProps {
  segments: TranscriptSegmentResponse[];
  speakers: SpeakerResponse[];
  currentTimeMs: number;
  onSeek: (timeMs: number) => void;
  transcriptId?: string;
  /** Search query from parent - filters segments */
  searchQuery?: string;
  /** Auto-scroll state from parent */
  autoScroll?: boolean;
  /** Callback when auto-scroll should be changed */
  onAutoScrollChange?: (enabled: boolean) => void;
  /** Whether speakers have been confirmed - enables inline chat when true */
  speakersConfirmed?: boolean;
  /** Map of segment sequence_order to key moment type */
  keyMomentMap?: Map<number, "tension" | "positive">;
}

/**
 * Speaker color palette for visual distinction
 */
const SPEAKER_COLORS = [
  "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
  "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
  "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200",
  "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200",
  "bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200",
  "bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200",
];

export const TranscriptView = forwardRef<
  TranscriptViewHandle,
  TranscriptViewProps
>(function TranscriptView(
  {
    segments,
    speakers,
    currentTimeMs,
    onSeek,
    transcriptId,
    searchQuery = "",
    autoScroll = true,
    onAutoScrollChange,
    speakersConfirmed = true,
    keyMomentMap,
  },
  ref
) {
  // Inline chat state - which segment has chat open (null = none)
  const [openChatSegmentId, setOpenChatSegmentId] = useState<string | null>(
    null
  );
  // Track dynamically updated message counts (when chats are opened)
  const [dynamicMessageCounts, setDynamicMessageCounts] = useState<
    Record<string, number>
  >({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeSegmentRef = useRef<HTMLDivElement>(null);
  const userScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number>();

  // Expose scrollToSegment via imperative handle
  useImperativeHandle(
    ref,
    () => ({
      scrollToSegment(segmentId: string) {
        const el = document.querySelector(`[data-segment-id="${segmentId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      },
    }),
    []
  );

  // Get segment IDs for prefetching chat counts
  const segmentIds = useMemo(() => segments.map((s) => s.id), [segments]);

  // Prefetch message counts for all segments
  const { messageCounts: prefetchedCounts } = useSegmentChatCounts(
    transcriptId,
    segmentIds
  );

  // Merge prefetched counts with dynamic counts (dynamic takes precedence)
  const messageCounts = useMemo(
    () => ({ ...prefetchedCounts, ...dynamicMessageCounts }),
    [prefetchedCounts, dynamicMessageCounts]
  );

  // Fetch annotations for the transcript
  const { data: annotationsData } = useAnnotations(transcriptId, {
    enabled: !!transcriptId,
  });

  // Map annotations by segment ID for easier lookup (converted to summary format)
  const annotationsBySegment = useMemo(() => {
    if (!annotationsData?.annotations) return {};

    const map: Record<string, SegmentAnnotationSummary[]> = {};

    for (const annotation of annotationsData.annotations) {
      if (annotation.segment_id) {
        if (!map[annotation.segment_id]) {
          map[annotation.segment_id] = [];
        }
        map[annotation.segment_id].push({
          id: annotation.id,
          annotation_type: annotation.annotation_type,
          severity: annotation.severity,
          title: annotation.title,
        });
      }
    }
    return map;
  }, [annotationsData]);

  // Handle inline chat toggle
  const handleChatToggle = useCallback((segmentId: string | null) => {
    setOpenChatSegmentId(segmentId);
  }, []);

  // Handle message count changes from inline chat
  const handleMessageCountChange = useCallback(
    (segmentId: string, count: number) => {
      setDynamicMessageCounts((prev) => {
        if (prev[segmentId] === count) return prev;
        return { ...prev, [segmentId]: count };
      });
    },
    []
  );

  // Handle ESC key to close inline chat
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openChatSegmentId) {
        setOpenChatSegmentId(null);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [openChatSegmentId]);

  // Find active segment based on current time
  const activeSegmentId = useMemo(() => {
    for (const segment of segments) {
      if (
        currentTimeMs >= segment.start_time_ms &&
        currentTimeMs < segment.end_time_ms
      ) {
        return segment.id;
      }
    }
    return null;
  }, [segments, currentTimeMs]);

  // Filter segments by search query (searches text, speaker label, and custom names)
  const filteredSegments = useMemo(() => {
    if (!searchQuery.trim()) return segments;
    const query = searchQuery.toLowerCase();

    // Build a map of speaker labels to custom names for searching
    // Use external_label as key since that matches segment.speaker_label
    const speakerNames: Record<string, string> = {};
    speakers.forEach((speaker) => {
      if (speaker.custom_name) {
        speakerNames[speaker.external_label] =
          speaker.custom_name.toLowerCase();
      }
    });

    return segments.filter((s) => {
      const customName = speakerNames[s.speaker_label];
      return (
        s.text.toLowerCase().includes(query) ||
        s.speaker_label?.toLowerCase().includes(query) ||
        customName?.includes(query)
      );
    });
  }, [segments, searchQuery, speakers]);

  // Map speakers to colors (use external_label as key to match segment.speaker_label)
  const speakerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakers.forEach((speaker, index) => {
      map[speaker.external_label] =
        SPEAKER_COLORS[index % SPEAKER_COLORS.length];
    });
    return map;
  }, [speakers]);

  // Map speaker labels to display names (custom_name takes precedence)
  // Use external_label as key since that matches segment.speaker_label ("A", "B")
  const speakerDisplayNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakers.forEach((speaker) => {
      map[speaker.external_label] =
        speaker.custom_name || speaker.display_label;
    });
    return map;
  }, [speakers]);

  // Auto-scroll to active segment
  useEffect(() => {
    if (
      autoScroll &&
      activeSegmentId &&
      activeSegmentRef.current &&
      !userScrollingRef.current
    ) {
      activeSegmentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [activeSegmentId, autoScroll]);

  // Detect user scrolling
  const handleScroll = useCallback(() => {
    userScrollingRef.current = true;

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }

    // Reset user scrolling flag after 1 second of no scrolling
    scrollTimeoutRef.current = window.setTimeout(() => {
      userScrollingRef.current = false;
    }, 1000);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle segment click
  const handleSegmentClick = useCallback(
    (segment: TranscriptSegmentResponse) => {
      userScrollingRef.current = false;
      onAutoScrollChange?.(true);
      onSeek(segment.start_time_ms);
    },
    [onSeek, onAutoScrollChange]
  );

  // Handle sentence click (also re-enables auto-scroll)
  const handleSentenceClick = useCallback(
    (timeMs: number) => {
      userScrollingRef.current = false;
      onAutoScrollChange?.(true);
      onSeek(timeMs);
    },
    [onSeek, onAutoScrollChange]
  );

  return (
    <div className="space-y-4">
      {/* Segments list - no internal scroll, page scrolls naturally */}
      <div
        ref={scrollContainerRef}
        className="space-y-3"
        onScroll={handleScroll}
      >
        {filteredSegments.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {searchQuery
              ? "No matching segments found"
              : "No transcript available"}
          </p>
        ) : (
          filteredSegments.map((segment) => (
            <div
              key={segment.id}
              ref={segment.id === activeSegmentId ? activeSegmentRef : null}
            >
              <TranscriptSegment
                segment={segment}
                isActive={segment.id === activeSegmentId}
                speakerColor={
                  speakerColorMap[segment.speaker_label] || SPEAKER_COLORS[0]
                }
                speakerDisplayName={
                  speakerDisplayNameMap[segment.speaker_label] ||
                  segment.speaker_label
                }
                currentTimeMs={currentTimeMs}
                onClick={() => handleSegmentClick(segment)}
                onSentenceClick={handleSentenceClick}
                annotations={annotationsBySegment[segment.id]}
                isChatOpen={openChatSegmentId === segment.id}
                onChatToggle={speakersConfirmed ? handleChatToggle : undefined}
                transcriptId={transcriptId}
                chatMessageCount={messageCounts[segment.id]}
                onMessageCountChange={handleMessageCountChange}
                searchQuery={searchQuery}
                speakersConfirmed={speakersConfirmed}
                showBlockLabel={true}
                keyMomentType={
                  keyMomentMap?.get(segment.sequence_order) ?? null
                }
              />
            </div>
          ))
        )}
      </div>

      {/* Search results count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          {filteredSegments.length} segment
          {filteredSegments.length !== 1 ? "s" : ""} found
        </p>
      )}
    </div>
  );
});
